
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useToast } from "@/components/ui/use-toast";
import { useState, useEffect } from "react";
import { EditableTable, EditableTableColumn } from "@/components/ui/editable-table";
import { EditableSelectOption } from "@/components/ui/editable-select";

interface Employee {
  id: number;
  name: string;
  email: string;
  role: string;
  contract_type: string; // New field
  base_salary: number;   // New field
  transport: number;     // New field
  bonuses: number;       // New field
}

const employeeRoles = ['Admin', '2D', '3D', 'UI', 'Design', 'Audio', 'Tech Art', 'QA', 'Dev'] as const;
const employeeRoleOptions: EditableSelectOption<typeof employeeRoles[number]>[] = employeeRoles.map(role => ({
  label: role,
  value: role
}));

const contractTypes = ['Permanent', 'Service provider', 'Obra labor', 'Intern'] as const;
const contractTypeOptions: EditableSelectOption<typeof contractTypes[number]>[] = contractTypes.map(type => ({
  label: type,
  value: type
}));

export function EmployeeTable() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [companyId, setCompanyId] = useState<number | null>(null);

  useEffect(() => {
    const fetchCompanyId = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          console.error('No user found');
          return;
        }

        const { data: userInfo } = await supabase
          .from("users")
          .select("company_id")
          .eq("user_id", user.id)
          .maybeSingle();
        
        if (userInfo?.company_id) {
          setCompanyId(userInfo.company_id);
        }
      } catch (error) {
        console.error('Error in fetchCompanyId:', error);
      }
    };

    fetchCompanyId();
  }, []);

  const { data: employees, isLoading } = useQuery({
    queryKey: ["employees", companyId],
    queryFn: async () => {
      if (!companyId) return [];
      
      const { data, error } = await supabase
        .from("employees")
        .select("*")
        .eq("company_id", companyId)
        .order("name");
      
      if (error) throw error;
      return data;
    },
    enabled: !!companyId
  });

  const handleDelete = async (id: number) => {
    try {
      const { error } = await supabase
        .from("employees")
        .delete()
        .eq("id", id)
        .eq("company_id", companyId);

      if (error) throw error;

      await queryClient.invalidateQueries({ queryKey: ["employees", companyId] });

      toast({
        title: "Success",
        description: "Employee deleted successfully",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete employee",
        variant: "destructive",
      });
    }
  };

  const handleUpdate = async (id: number, field: keyof Employee, value: string) => {
    try {
      const { error } = await supabase
        .from("employees")
        .update({ [field]: value })
        .eq("id", id)
        .eq("company_id", companyId);

      if (error) throw error;

      await queryClient.invalidateQueries({ queryKey: ["employees", companyId] });

      toast({
        title: "Success",
        description: "Employee updated successfully",
      });
    } catch (error) {
      console.error('Error updating employee:', error);
      toast({
        title: "Error",
        description: "Failed to update employee",
        variant: "destructive",
      });
    }
  };

  const columns: EditableTableColumn<Employee>[] = [
    { key: 'name', header: 'Name', type: 'text' },
    { key: 'email', header: 'Email', type: 'text' },
    { key: 'role', header: 'Role', type: 'select', options: employeeRoleOptions },
    { key: 'contract_type', header: 'Contract Type', type: 'select', options: contractTypeOptions }, // Updated column
    { key: 'base_salary', header: 'Base Salary', type: 'currency' },
    { key: 'transport', header: 'Transport', type: 'currency' },
    { key: 'bonuses', header: 'Bonuses', type: 'currency' },
    { key: 'salary', header: 'Salary', type: 'calculated', subtype: 'currency', formula: (employee: Employee) => employee.base_salary + employee.transport + employee.bonuses },
    { key: 'with_benefits', header: 'w/ Benefits', type: 'calculated', subtype: 'currency', formula: (employee: Employee) => (employee.base_salary + employee.bonuses) * 1.42 + employee.transport }
  ];

  if (isLoading) {
    return <div className="p-8 text-center">Loading...</div>;
  }

  return (
    <div className="p-4">
      <EditableTable
        data={employees ?? []}
        columns={columns}
        onUpdate={handleUpdate}
        onDelete={handleDelete}
      />
    </div>
  );
}
